import * as UtilityAccordion from "../utilities/u-accordion";
import {convertCssAniDurationToMS, addClassForCloseAnimation} from "../utilities/helper-function";
import {getFocusableElements, trapFocus} from "../helpers/helper";

class Header {
    constructor(element) {
        this.body = document.querySelector('body');

        this.element = element;

        this.burgerBtn = this.element.querySelector(".burger-btn");
        this.burgerBtnAriaLabelOpen = this.burgerBtn?.dataset.ariaLabelOpen;
        this.burgerBtnAriaLabelClose = this.burgerBtn?.dataset.ariaLabelClose;
        this.headerPanel = this.element.querySelector('.header__panel');
        this.headerPanelBtns = this.element.querySelectorAll('button.header__panel-btn');
        this.headerPanelList = this.headerPanel?.querySelector('ul.header__panel-list')
        this.headerPanelBtns.forEach((btn) => {
            btn.nextElementSibling.setAttribute("inert", "true")
        })

        this.rootStyles = getComputedStyle(document.documentElement);
        this.aniFast = convertCssAniDurationToMS(this.rootStyles.getPropertyValue('--ani--fast').trim());
        this.aniSlow = convertCssAniDurationToMS(this.rootStyles.getPropertyValue('--ani--slow').trim());

        this.mobileBreakpoint = 768;
        this.isAccordionOpenByResize = false;
        this.isAccordionCloseByResize = false;

        this.headerPanelListInitialHeight = 0;
        this.headerPanel2ndHeight = 0;
        this.lastWindowSize = 0;

        if (this.headerPanel) {
            this.focusableElements = getFocusableElements(this.headerPanel);
            if (this.burgerBtn && !this.focusableElements.includes(this.burgerBtn)) {
                this.focusableElements.unshift(this.burgerBtn);
            }
        }
        if (this.burgerBtn) this.setBurgerBtnAriaLabel(this.isHeaderPanelOpen());

        /* Event Listeners */
        window.addEventListener('resize', () => {
            this.updatePanelLayerVisibilityByResize();
            this.lastWindowSize = window.innerWidth === 0 ? 0 : window.innerWidth;
        });

        this.burgerBtn?.addEventListener('click', () => this.toggleHeaderPanelVisibility());
        this.headerPanelBtns?.forEach((btn) => {
            btn.addEventListener('click', () => {
                if (this.isButtonSelected(btn)) {
                    // Only for Mobile and Desktop with Tab navigation
                    this.toggleCurrentBtnStatus(btn, true);
                    this.togglePanelLayerVisibility(btn.nextElementSibling);
                    this.clearCurrentlySelectedSiblingsStyles(btn);

                    if (this.isWindowMobileSize()) {
                        addClassForCloseAnimation(btn.nextElementSibling, "header__panel-list--hide", this.aniSlow);
                    }

                } else {
                    this.clearCurrentlySelectedSiblingsStyles(btn);
                    this.toggleCurrentBtnStatus(btn, false);
                    this.togglePanelLayerVisibility(btn.nextElementSibling);
                }
            })
        })

        document.addEventListener('keydown', (e) => {
            if (!this.burgerBtn) return;
            if (!this.isHeaderPanelOpen()) return;
            if (this.focusableElements) trapFocus(e, () => this.focusableElements);
            if (e.key === 'Escape') {
                e.preventDefault();
                this.toggleHeaderPanelVisibility();
            }
        }, true);
    }

    /* Show and Hide Header Panel */
    toggleHeaderPanelVisibility() {
        this.headerPanel.classList.remove("header__panel--hide");
        this.headerPanel.classList.toggle("header__panel--show");
        const isHeaderPanelOpen = this.isHeaderPanelOpen();
        if (!isHeaderPanelOpen) {
            addClassForCloseAnimation(this.headerPanel, "header__panel--hide", this.aniFast);
            this.clearAllCurrentStatus();
            this.headerPanelListInitialHeight = 0;
            this.headerPanel2ndHeight = 0;
            this.body.classList.remove('body--not-scrollable');
        } else {
            this.headerPanelListInitialHeight = this.headerPanelList.scrollHeight;
            this.headerPanel2ndHeight = this.headerPanelListInitialHeight;
            this.body.classList.add('body--not-scrollable')
        }
        this.toggleBurgerBtnIcon(isHeaderPanelOpen);
        this.focusableElements = getFocusableElements(this.headerPanel);
        if (this.burgerBtn && !this.focusableElements.includes(this.burgerBtn)) {
            this.focusableElements.unshift(this.burgerBtn);
        }
    }

    setBurgerBtnAriaLabel(isHeaderPanelOpen) {
        const ariaLabelText = isHeaderPanelOpen ? this.burgerBtnAriaLabelClose : this.burgerBtnAriaLabelOpen;
        this.burgerBtn.setAttribute('aria-label', ariaLabelText);
    }

    /* Change Icon for Burger Menu Button */
    toggleBurgerBtnIcon(isHeaderPanelOpen) {
        this.setBurgerBtnAriaLabel(isHeaderPanelOpen);
        this.burgerBtn.classList.toggle("burger-btn--nav-opened");
        this.burgerBtn.setAttribute('aria-expanded', isHeaderPanelOpen);
    }

    /* Manipulate Current Button Status and Panel Visibility */
    clearAllCurrentStatus() {
        this.headerPanelBtns.forEach((btn) => {
            if (this.isButtonSelected(btn)) {
                this.toggleCurrentBtnStatus(btn, true);
                this.togglePanelLayerVisibility(btn.nextElementSibling);
            }
        })
    }

    clearCurrentlySelectedSiblingsStyles(btn) {
        const parentList = btn.closest('.header__panel-list');
        const currentSelectedBtns = parentList.querySelectorAll('.header__panel-btn--current');
        currentSelectedBtns.forEach((selectedBtn) => {
            this.toggleCurrentBtnStatus(selectedBtn, true);
            this.togglePanelLayerVisibility(selectedBtn.nextElementSibling);
        })
    }

    toggleCurrentBtnStatus(btn, isButtonSelected) {
        btn.classList.toggle('header__panel-btn--current');
        btn.setAttribute('aria-expanded', !isButtonSelected);
    }

    togglePanelLayerVisibility(panelLayer) {
        panelLayer.toggleAttribute('inert');
        panelLayer.classList.toggle('header__panel-list--show');

        if (this.isWindowMobileSize(panelLayer)) {
            UtilityAccordion.slideToggle(panelLayer, this.aniSlow);
        } else {
            this.updateHeaderPanelListHeight(panelLayer);
        }
        this.focusableElements = getFocusableElements(this.headerPanel);
        if (this.burgerBtn && !this.focusableElements.includes(this.burgerBtn)) {
            this.focusableElements.unshift(this.burgerBtn);
        }
    }

    updatePanelLayerVisibilityByResize() {
        // Resize window to smaller
        if (window.innerWidth < this.lastWindowSize) {
            if (this.isWindowMobileSize()) {
                // Call only once when the window size becomes smaller than
                // mobile breakpoint
                if (!this.isAccordionOpenByResize) {
                    this.headerPanelBtns.forEach((btn) => {
                        if (this.isButtonSelected(btn)) {
                            UtilityAccordion.slideDown(btn.nextElementSibling, this.aniSlow);
                            this.isAccordionOpenByResize = true;
                        }
                    })
                }
                this.isAccordionCloseByResize = false;
                this.headerPanelListInitialHeight = 0;
                this.headerPanel2ndHeight = 0;
            }
        } else {
            if (!this.isWindowMobileSize()) {
                // Call only once when the window size becomes bigger than
                // mobile breakpoint
                if (!this.isAccordionCloseByResize) {
                    this.headerPanelBtns.forEach((btn) => {
                        if (this.isButtonSelected(btn)) {
                            // Remove properties set by SlideDown utility
                            // function
                            btn.nextElementSibling.style.removeProperty('display');
                            btn.nextElementSibling.style.removeProperty('box-sizing');
                            this.isAccordionCloseByResize = true;
                        }
                    })
                }
                this.isAccordionOpenByResize = false;
                this.headerPanelListInitialHeight = this.headerPanelList.scrollHeight;

                // check if open panel has and update height
                const openedPanels = this.headerPanel.querySelectorAll('.header__panel-list--show');
                openedPanels.forEach((panel) => {
                    this.updateHeaderPanelListHeight(panel);
                })
            }
        }
    }

    updateHeaderPanelListHeight(panelLayer) {
        if (panelLayer.classList.contains('header__panel-list--2nd-level')) {
            if (panelLayer.scrollHeight > this.headerPanelListInitialHeight) {
                this.headerPanelList.style.height = panelLayer.scrollHeight + "px";
                this.headerPanel2ndHeight = panelLayer.scrollHeight;
            } else {
                this.headerPanelList.style.height = this.headerPanelListInitialHeight + "px";
                this.headerPanel2ndHeight = this.headerPanelListInitialHeight;
            }
        } else {
            if (panelLayer.scrollHeight > this.headerPanel2ndHeight) {
                this.headerPanelList.style.height = panelLayer.scrollHeight + "px";
            } else {
                if (this.headerPanel2ndHeight <= this.headerPanelListInitialHeight) {
                    this.headerPanelList.style.height = this.headerPanelListInitialHeight + "px";
                }
            }
        }
    }

    /* Helper Functions */
    isHeaderPanelOpen() {
        return this.headerPanel?.classList.contains('header__panel--show');
    }

    isButtonSelected(btn) {
        return btn.classList.contains('header__panel-btn--current');
    }

    isWindowMobileSize() {
        return window.innerWidth < this.mobileBreakpoint;
    }
}

export default Header;